import React from 'react';
import { Box, Card, CardContent, Typography } from '@mui/material';
import { Moment } from 'moment-timezone';
import { JInfo, ParsedSensorInfo } from '../atoms-types/eSensors';
import {
  formatLastSeen,
  isSensorOffline,
  SENSOR_OFFLINE_BORDER_COLOR,
  SENSOR_OFFLINE_COLOR,
  SENSOR_ONLINE_COLOR,
} from '../atoms-types/sensorFreshness';
import { convertWindDirection } from '../converters/windDirectionConverter';
import SensorIdentityTooltip from '../hvac/SensorIdentityTooltip';
import SensorSparkline from '../hvac/SensorSparkline';
import { getDisplayClass, getDisplayLocation } from '../hvac/hvacStatus';
import { useSensorSparkline } from '../hvac/useSensorSparkline';

const getUnit = (displayType: string) => {
  switch (displayType) {
    case 'TempC':
      return '°C';
    case 'TempF':
      return '°F';
    case 'Humidity':
    case 'Moisture':
      return '%';
    case 'WindAvg':
    case 'WindMax':
      return 'mph';
    case 'WindDir':
      return '°';
    case 'BaroPress':
      return 'hPa';
    case 'Height':
    case 'Inches':
      return 'in';
    case 'Gallons':
    case 'FuelVolume':
      return 'gal';
    default:
      return '';
  }
};

const readNumeric = (data: JInfo | Record<string, unknown>, field: string): number | null => {
  const raw = (data as Record<string, unknown>)[field];
  return typeof raw === 'number' && !Number.isNaN(raw) ? raw : null;
};

const formatPrimary = (field: string, value: number | null): string => {
  if (value === null) return '—';
  if (field === 'WindDir') {
    return `${value.toFixed(0)}° ${convertWindDirection(value).direction}`;
  }
  const unit = getUnit(field);
  const decimals = field === 'Humidity' || field === 'Moisture' || field.startsWith('Temp') ? 1 : 2;
  return `${value.toFixed(decimals)}${unit}`;
};

interface EnvironSensorCardProps {
  sensor: ParsedSensorInfo;
  cardType: 'temp' | 'wind' | null;
  cardDisplays: string[];
  selected?: boolean;
  startDateTime: Moment;
  endDateTime: Moment;
  onSelect: () => void;
}

/**
 * Compact HVAC-style sensor card: primary reading, identity, sparkline, last seen.
 * Full metric history lives in the adjacent chart when selected.
 */
const EnvironSensorCard: React.FC<EnvironSensorCardProps> = ({
  sensor,
  cardType,
  cardDisplays,
  selected = false,
  startDateTime,
  endDateTime,
  onSelect,
}) => {
  const offline = isSensorOffline(sensor.LastRx);
  const lastSeen = formatLastSeen(sensor.LastRx);
  const statusColor = offline ? SENSOR_OFFLINE_COLOR : SENSOR_ONLINE_COLOR;
  const borderColor = offline
    ? SENSOR_OFFLINE_BORDER_COLOR
    : selected
      ? statusColor
      : 'divider';
  const name = getDisplayLocation(sensor) || sensor.deviceEUI.slice(-6);
  const classLabel = getDisplayClass(sensor);
  const classWithSubtype = classLabel
    ? `${classLabel}${cardType === 'temp' ? ' (Temp)' : cardType === 'wind' ? ' (Wind)' : ''}`
    : cardType === 'temp'
      ? 'Temp'
      : cardType === 'wind'
        ? 'Wind'
        : '';

  const primaryField = cardDisplays[0] ?? '';
  const liveValue = offline ? null : readNumeric(sensor.jInfo, primaryField);
  const primaryText = formatPrimary(primaryField, liveValue);

  // Prefer a stable numeric series for the sparkline (skip WindDir when possible).
  const sparkField =
    primaryField === 'WindDir'
      ? (cardDisplays.find((d) => d !== 'WindDir') ?? primaryField)
      : primaryField;
  const sparkLive = offline ? null : readNumeric(sensor.jInfo, sparkField);

  const { points } = useSensorSparkline(
    sensor.deviceEUI,
    sparkField,
    startDateTime,
    endDateTime,
    sparkLive,
  );

  return (
    <Card
      onClick={onSelect}
      elevation={selected ? 4 : 1}
      sx={{
        minHeight: 148,
        height: '100%',
        cursor: 'pointer',
        overflow: 'visible',
        border: '2px',
        borderStyle: offline ? 'dashed' : 'solid',
        borderColor,
        bgcolor: selected ? `${statusColor}14` : 'background.paper',
        transition: 'border-color 120ms ease, box-shadow 120ms ease, background-color 120ms ease',
        '&:hover': {
          borderColor: offline ? SENSOR_OFFLINE_BORDER_COLOR : statusColor,
          boxShadow: 3,
        },
      }}
    >
      <CardContent
        sx={{
          p: 1.25,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.75,
          '&:last-child': { pb: 1.25 },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                bgcolor: statusColor,
                flexShrink: 0,
              }}
            />
            <Typography
              variant="h6"
              noWrap
              title={primaryField ? `${primaryField}: ${primaryText}` : undefined}
              sx={{
                fontWeight: 700,
                lineHeight: 1.1,
                color: offline ? SENSOR_OFFLINE_COLOR : 'text.primary',
              }}
            >
              {primaryText}
            </Typography>
          </Box>
          <Typography
            variant="caption"
            sx={{
              color: statusColor,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: 0.4,
              flexShrink: 0,
            }}
          >
            {offline ? 'Offline' : 'Online'}
          </Typography>
        </Box>

        <Box sx={{ minWidth: 0, opacity: offline ? 0.85 : 1 }}>
          <SensorIdentityTooltip sensor={sensor}>
            <Typography
              variant="body2"
              noWrap
              sx={{
                fontWeight: 600,
                color: offline ? SENSOR_OFFLINE_COLOR : 'text.primary',
                lineHeight: 1.25,
              }}
            >
              {name}
            </Typography>
          </SensorIdentityTooltip>
          {classWithSubtype ? (
            <Typography
              variant="caption"
              noWrap
              title={classWithSubtype}
              sx={{
                color: offline ? SENSOR_OFFLINE_COLOR : 'text.secondary',
                lineHeight: 1.25,
                display: 'block',
              }}
            >
              {classWithSubtype}
            </Typography>
          ) : null}
        </Box>

        <Box
          sx={{
            width: '100%',
            height: 24,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <SensorSparkline points={points} color={statusColor} width={120} height={24} />
        </Box>

        <Typography
          variant="caption"
          noWrap
          title={`Last seen ${lastSeen}`}
          sx={{
            color: offline ? SENSOR_OFFLINE_COLOR : 'text.secondary',
            fontWeight: offline ? 600 : 400,
            display: 'block',
            lineHeight: 1.35,
          }}
        >
          Last seen {lastSeen}
        </Typography>
      </CardContent>
    </Card>
  );
};

export default EnvironSensorCard;
